import TRTC, { Client, LocalStream } from 'trtc-js-sdk';
import { GetVoiceSign } from '../typings/live/GetVoiceSign';
import { RTCType } from '../typings/rtc/type';
import logger from '../utils/logger';

import { uuid } from '../utils/uuid';
import { CoreRTC } from './CoreRTC';

/**
 * TRTC 实例接口（内部使用）
 */
export interface ITrtcInstance {
  /**
   * TRTC 客户端实例
   */
  client: Client | null;
  /**
   * 本地音视频流
   */
  stream: LocalStream | null;
}

/**
 * 浏览器端 TRTC RTC 实现
 * 
 * 基于腾讯云 TRTC SDK 实现的浏览器端 RTC 类。
 * 适用于在浏览器环境中进行实时语音通信。
 * 
 * @example
 * ```typescript
 * const rtc = new BrowserRTC({
 *   getVoiceSign: async (params) => {
 *     const response = await fetch('/api/get-voice-sign', {
 *       method: 'POST',
 *       body: JSON.stringify(params)
 *     })
 *     return response.json()
 *   },
 *   autoPlayConfirm: (opts) => {
 *     // 处理自动播放确认
 *     opts.onConfirm()
 *   }
 * })
 * 
 * // 加入房间
 * await rtc.join({
 *   voiceId: 'voice-id',
 *   userId: 'user-id',
 *   roomId: 'room-id',
 *   type: RTCType.RealseeBrowserRTC
 * })
 * 
 * // 切换麦克风
 * await rtc.toggleMicro(true)
 * ```
 */
export class BrowserRTC extends CoreRTC {
  type = RTCType.RealseeBrowserRTC;
  joined = false;
  micro = false;
  voiceId = '';

  private _getVoiceSign: GetVoiceSign = () => {
    throw new Error('方法 getVoiceSign 未实现');
  };

  private _autoPlayConfirm: (opts: {
    onConfirm: () => void;
  }) => void | undefined;

  /**
   * 检查系统要求
   * 
   * 检查浏览器是否满足 TRTC 的系统要求。
   * 
   * @returns 系统要求检查结果
   */
  public checkRequirement = () => TRTC.checkSystemRequirements();

  /**
   * DOM 容器（内部使用）
   */
  private $dom: HTMLDivElement = document.createElement('div');

  /**
   * TRTC 实例（内部使用）
   */
  private _trtc: ITrtcInstance = {
    client: null,
    stream: null,
  };

  /**
   * 创建 BrowserRTC 实例
   * 
   * @param options 配置选项
   * @param options.getVoiceSign 获取语音签名的函数，必需
   * @param options.autoPlayConfirm 自动播放确认回调（可选），用于处理浏览器的自动播放策略
   */
  constructor(options: {
    getVoiceSign: GetVoiceSign;
    autoPlayConfirm?: (opts: { onConfirm: () => void }) => void;
  }) {
    super();
    this._getVoiceSign = options.getVoiceSign;
    // @ts-ignore
    this._autoPlayConfirm = options.autoPlayConfirm;
    this._initParentDom();
  }

  private _initParentDom() {
    this.$dom.id = `rs-rtc-${uuid()}`;
    this.$dom.style.display = 'none';
    document.body.appendChild(this.$dom);
  }

  join = async (options: {
    voiceId: string;
    userId: string;
    roomId: string;
    type: RTCType;
  }): Promise<boolean> => {
    this.emit('joinWillStart');
    const createClientOptions = await this._getVoiceSign({
      voiceId: options.voiceId,
      roomId: options.roomId,
      userId: options.userId,
    });
    // 1.  创建client
    this._trtc.client = TRTC.createClient({
      mode: 'rtc',
      ...createClientOptions,
      enableAutoPlayDialog:
        typeof this._autoPlayConfirm === 'function' ? false : true,
    });

    this._trtc.client?.enableAudioVolumeEvaluation();

    // 2. 订阅事件
    this._registerRtcEvent();

    // 3. 加入语音房间
    let result = await new Promise<boolean>((resolve) => {
      this._trtc.client
        ?.join({ roomId: Number(options.voiceId) })
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });

    this.joined = result;

    if (!result) return false; // 进房失败

    // 4. 创建本地流
    this._trtc.stream = TRTC.createStream({
      userId: createClientOptions.userId,
      audio: true,
      video: false,
    });

    // 5. 初始化本地流
    result = await new Promise<boolean>((resolve) => {
      this._trtc.stream
        ?.initialize()
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });

    // 6. 发布本地流
    result = await new Promise<boolean>((resolve) => {
      this._trtc.client
        ?.publish(this._trtc.stream!)
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });

    this.joined = result;

    if (this.joined) this.emit('joined');

    return result;
  };

  quit(): Promise<boolean> {
    return new Promise(async (resolve) => {
      this.joined = false;
      if (this._trtc.stream) {
        await this._trtc.client?.unpublish(this._trtc.stream);
        this._trtc.stream.stop();
        this._trtc.stream.close();
        this._trtc.stream = null;
      }
      this._trtc.client
        ?.leave()
        .then(() => (this._trtc.client = null))
        .then(() => resolve(true))
        .catch(() => resolve(false));
      resolve(true);
    });
  }

  detectMicro(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const rule = { audio: true, video: false };
      try {
        navigator.mediaDevices
          .getUserMedia(rule)
          .then(() => resolve(true))
          .catch((e) => {
            logger.error(e.message);
            resolve(false);
          });
      } catch (e) {
        console.error(e);
        resolve(false);
      }
    });
  }

  toggleMicro(flag?: boolean): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      try {
        if (flag) {
          this._trtc?.stream?.unmuteAudio();
          this.micro = true;
        } else {
          this._trtc?.stream?.muteAudio();
          this.micro = false;
        }
        resolve(true);
      } catch (e) {
        resolve(false);
      }
    });
  }

  shock(): void {
    throw new Error('Method not implemented.');
  }

  private _registerRtcEvent() {
    this._trtc.client?.on('stream-added', this._handleStreamAdded);
    this._trtc.client?.on('stream-subscribed', this._handleStreamSubscribed);
    this._trtc.client?.on('audio-volume', (event) =>
      this.emit(
        'userVolumes',
        event.result.map(({ userId, audioVolume }) => ({
          userId,
          audioVolume,
        })),
      ),
    );
    this._trtc.client?.on('error', (event: any) => this.emit('error', event));
  }

  private _handleStreamAdded = (event: any) => {
    const remoteStream = event.stream;
    logger.info('远端流增加:', remoteStream.getId());
    //订阅远端流
    this._trtc.client?.subscribe(remoteStream);
  };

  private _handleStreamSubscribed = (event: any) => {
    const remoteStream = event.stream;
    logger.info('远端流订阅成功：', remoteStream.getId());
    const playId = remoteStream.getId();
    const domId = `rs-rtc-remote_stream-${playId}`;
    const dom = document.createElement('div');
    dom.id = domId;
    this.$dom.appendChild(dom);
    // 播放远端流
    if (typeof this._autoPlayConfirm === 'function') {
      remoteStream.play(domId).catch((error: any) => {
        logger.warn('远端流播放失败（将尝试用户确认恢复）')
      });
      remoteStream.on('error', (error: any) => {
        const errorCode = error.getCode();
        if (errorCode === 0x4043) {
          // PLAY_NOT_ALLOWED,引导用户手势操作并调用 stream.resume 恢复音视频播放
          const onConfirm = () => remoteStream.resume();
          this._autoPlayConfirm({ onConfirm });
        }
      });
    } else {
      remoteStream.play(domId);
    }
  };
}
